import h from 'hyperscript';

import { createAnchorButton } from '@disy/ui/button';
import type { MenuContent, MenuItemGroup } from '@disy/ui/menu';
import { setupMenuButton } from '@disy/ui/menu';
import { createMoreActionsButton } from '@disy/ui/more-actions-button';

import { Select } from 'ui/select/select';

import { AttributeStatus } from 'cadenza/repository/model/attribute-status.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AnalysisContextHierarchyDto } from 'cadenza/workbook/model/analysis-context-hierarchy-dto';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id.gen';
import { getAffectedViewAndDataViewByHierarchyRelations } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { AffectedViewsLabel } from 'cadenza/workbook/workbook-panel/analysis-context/affected-views-label/affected-views-label';

import i18n from './create-hierarchy.properties';

interface CreateHierarchyOptions {
  withMoreButton?: boolean;
  withAffectedViews?: boolean;
  initiallyFocused?: boolean;
  moreActionsContentFn?: () => MenuItemGroup;
}

export function createHierarchy(
  hierarchy: WorkbookAttributeDto | AnalysisContextHierarchyDto,
  {
    withMoreButton = true,
    withAffectedViews = true,
    initiallyFocused = false,
    moreActionsContentFn,
  }: CreateHierarchyOptions = {},
) {
  const isInvalid = hierarchy.status !== 'OK';
  const hierarchySelect = new Select({
    // ! – because the core assumption is that `hierarchy` is a hierarchy
    data: hierarchy.levels!.map((level) => ({
      id: level.name,
      selected: level.name === hierarchy.selectedLevelName,
      text: level.printName,
    })),
  });
  // cast – `withAffectedViews` implies `AnalysisContextHierarchyDto`
  const hasAffectedViews = isHierarchyAffectingViews(hierarchy.id as HierarchyId);
  hierarchySelect.disabled = (withAffectedViews && !hasAffectedViews) || isInvalid;
  if (initiallyFocused) {
    setTimeout(() => hierarchySelect.focus());
  }

  const resetButton = createAnchorButton(i18n('reset'), { styleClass: 'd-hierarchy--reset', hidden: true });
  const hierarchyLabel = h<HTMLLabelElement>(
    'label.d-condition--name',
    {
      id: `${hierarchy.id}--label`,
      onclick: () => hierarchySelect.focus(),
    },
    isInvalid ? i18n('invalid.hierarchy.title') : hierarchy.printName,
  );
  hierarchyLabel.classList.toggle('is-disabled', hierarchySelect.disabled);
  hierarchyLabel.classList.toggle('is-invalid', isInvalid);
  hierarchySelect.labelledBy = hierarchyLabel;

  let moreActionsButton: HTMLElement | undefined;
  if (withMoreButton && moreActionsContentFn) {
    moreActionsButton = createMoreActionsButton(hierarchy.printName, { size: 's' });
    // cast – eventually this argument gets passed into `getPopupContent<T> (content: PopupContentFunction<T>, ...)`
    setupMenuButton(moreActionsButton, moreActionsContentFn as unknown as MenuContent);
  }

  let affectedViewsLabel: HTMLElement | undefined;
  if (withAffectedViews && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context')) {
    affectedViewsLabel = new AffectedViewsLabel({ hierarchyId: hierarchy.id as HierarchyId });
  }

  const hierarchyComponent = h<HTMLLIElement>(
    'li.li-condition-wrapper.d-hover-context',
    { 'data-id': hierarchy.id },
    h('.d-condition--header', [
      hierarchyLabel,
      h('.d-inline-stack', [resetButton, moreActionsButton, affectedViewsLabel]),
    ]),
    isInvalid ? h('.d-alert.d-alert-small.d-alert-error', AttributeStatus.i18n(hierarchy.status)) : hierarchySelect,
  );
  hierarchyComponent.classList.toggle('is-invalid', isInvalid);
  return hierarchyComponent;
}

function isHierarchyAffectingViews(hierarchyId: HierarchyId) {
  return getAffectedViewAndDataViewByHierarchyRelations(getStore().getState(), hierarchyId).affectedViews.length > 0;
}
